/**
 * Utility functions and constants for sender trust analysis
 */
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faShieldCheck,
  faShieldExclamation,
  faShieldXmark,
  faUserCheck,
  faUserXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  TrustLevel,
  type SenderTrustDecision,
} from '../../../api/cmail/cmail.dto';

export interface SenderTrustIcon {
  readonly icon: IconDefinition;
  readonly color: string;
}

export const getTrustLevelIcon = (trustLevel: TrustLevel): SenderTrustIcon => {
  switch (trustLevel) {
    case TrustLevel.HIGH:
      return { icon: faShieldCheck, color: 'var(--cmail-trust-green)' };
    case TrustLevel.MEDIUM:
      return {
        icon: faShieldExclamation,
        color: 'var(--mantine-color-yellow-6)',
      };
    case TrustLevel.LOW:
      return { icon: faShieldXmark, color: 'var(--mantine-color-red-6)' };
    default:
      return { icon: faShieldCheck, color: 'var(--mantine-color-dimmed)' };
  }
};

/** The "Verified User" tooltip row, or null when the sender carries no verification result. */
export const getVerifiedUserIcon = (
  userVerified: boolean | undefined
): (SenderTrustIcon & { readonly label: string }) | null => {
  if (userVerified === true) {
    return {
      icon: faUserCheck,
      color: 'var(--cmail-trust-green)',
      label: 'Yes',
    };
  }
  if (userVerified === false) {
    return {
      icon: faUserXmark,
      color: 'var(--cmail-trust-warning)',
      label: 'No',
    };
  }
  return null;
};

/**
 * The single icon shown for a sender, in the inbox table and the message header alike.
 * An unverified sender outranks the trust shield unless trust is LOW, so a red shield is never hidden.
 */
export const getSenderTrustIcon = (
  senderTrustDecision: SenderTrustDecision | undefined,
  userVerified: boolean | undefined
): SenderTrustIcon | null => {
  if (
    userVerified === false &&
    senderTrustDecision?.trustLevel !== TrustLevel.LOW
  ) {
    return getVerifiedUserIcon(false);
  }
  if (senderTrustDecision) {
    return getTrustLevelIcon(senderTrustDecision.trustLevel);
  }
  if (userVerified === true) {
    return getVerifiedUserIcon(true);
  }
  return null;
};

/**
 * Flags that are "negative" - where false is good and true is bad
 */
export const NEGATIVE_FLAGS = [
  'reply_to_divergent',
  'return_path_divergent',
  'lookalike_detected',
  'suspicious_helo',
] as const;

/**
 * Type guard to check if a string is a negative flag
 */
export const isNegativeFlag = (
  flag: string
): flag is (typeof NEGATIVE_FLAGS)[number] => {
  return (NEGATIVE_FLAGS as readonly string[]).includes(flag);
};

/**
 * Acronyms that should be displayed in all caps
 */
const ACRONYMS = ['spf', 'dkim', 'dmarc', 'mx', 'tls', 'helo'] as const;

/**
 * Formats a flag name for display, handling acronyms and negative flags
 * @param flag - The flag name to format
 * @param value - The boolean value of the flag
 * @returns A formatted, human-readable flag name
 */
export const formatFlagName = (flag: string, value: boolean): string => {
  const isFlagNegative = isNegativeFlag(flag);

  let displayName = flag
    .split('_')
    .map((word) => {
      const lowerWord = word.toLowerCase();
      // Keep acronyms in all caps
      if (ACRONYMS.includes(lowerWord as (typeof ACRONYMS)[number])) {
        return word.toUpperCase();
      }
      // Regular word capitalization
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  // Add "No" prefix for negative flags when they're false (which is good)
  if (isFlagNegative && !value) {
    displayName = `No ${displayName}`;
  }

  return displayName;
};
